/**
 * Unified entity renderer — single iteration over the entity registry,
 * dispatching each entity to the correct visual node.
 *
 * Individual node components (MeshNode, PrimitiveNode, LightNode,
 * MannequinNode) live in their own files — this file handles
 * composition, shared state (texture cache), and entity dispatch.
 */

import { useFrame, useThree } from "@react-three/fiber";
import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import * as THREE from "three/webgpu";
import { useActiveRenderConfig } from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type { BlendFile } from "../blend-reader/types/BlendFile.ts";
import {
  type BlendObject,
  ObjectType,
} from "../blend-reader/types/BlendObject.ts";
import type { Entity } from "../ecs/index.ts";
import { entityIdToStoreKey, useEntityRegistry } from "../ecs/index.ts";
import { ImagePlaneNode } from "./ImagePlaneRenderer.tsx";
import { MannequinNode } from "./MannequinRenderer.tsx";
import { initMaxAnisotropy } from "./materialCache.ts";
import {
  buildSceneObjectNameMap,
  mergeModifierStack,
  modifierStackCanGenerateGeometryWithoutBase,
} from "./modifiers/evaluation.ts";
import { EmptyNode, LightNode, PrimitiveNode } from "./PrimitiveRenderer.tsx";
import {
  buildTextureCache,
  disposeTextureCache,
  type LazyTexturePair,
  MeshNode,
} from "./SceneRenderer.tsx";

// ---------------------------------------------------------------------------
// Shared blend-file context — texture cache and morph keys, built once per
// blendFile and shared across all BlendMeshEntity instances.
// ---------------------------------------------------------------------------

interface BlendEntryCache {
  blendFile: BlendFile;
  objectsByName: Map<string, BlendObject>;
  textureCache: Map<bigint, LazyTexturePair>;
  morphKeysCache: Map<string, { name: string; deltas: Float32Array }[]>;
}

interface BlendContext {
  entries: ReadonlyMap<string, BlendEntryCache>;
}

const BlendCtx = createContext<BlendContext | null>(null);

function BlendContextProvider({ children }: { children: React.ReactNode }) {
  const blendFiles = useStore((s) => s.blendFiles);

  // Track previous per-entry caches for disposal
  const prevEntriesRef = useRef<Map<string, BlendEntryCache>>(new Map());

  const entries = useMemo(() => {
    const prevEntries = prevEntriesRef.current;
    const newEntries = new Map<string, BlendEntryCache>();

    // Dispose texture caches for entries that were removed
    for (const [id, cache] of prevEntries) {
      if (!blendFiles.has(id)) {
        disposeTextureCache(cache.textureCache);
      }
    }

    if (blendFiles.size === 0) {
      prevEntriesRef.current = newEntries;
      return newEntries;
    }

    for (const [entryId, entry] of blendFiles) {
      const prev = prevEntries.get(entryId);
      if (prev && prev.blendFile === entry.file) {
        // Reuse existing cache
        newEntries.set(entryId, prev);
      } else {
        // Build new caches for this entry
        const objectsByName = new Map<string, BlendObject>();
        for (const obj of entry.file.objects) {
          objectsByName.set(obj.name, obj);
        }
        const morphKeysCache = new Map<
          string,
          { name: string; deltas: Float32Array }[]
        >();
        for (const [setName, skSet] of entry.file.shapeKeySets) {
          morphKeysCache.set(
            setName,
            skSet.keys
              .filter((k) => k.index > 0)
              .map((k) => ({ name: k.name, deltas: k.deltas })),
          );
        }
        // Dispose old texture cache if entry was replaced
        if (prev) disposeTextureCache(prev.textureCache);
        const textureCache = buildTextureCache(entry.file.images);
        newEntries.set(entryId, {
          blendFile: entry.file,
          objectsByName,
          textureCache,
          morphKeysCache,
        });
      }
    }
    prevEntriesRef.current = newEntries;
    return newEntries;
  }, [blendFiles]);

  useEffect(() => {
    return () => {
      for (const cache of prevEntriesRef.current.values()) {
        disposeTextureCache(cache.textureCache);
      }
    };
  }, []);

  const ctx = useMemo<BlendContext | null>(
    () => (entries.size > 0 ? { entries } : null),
    [entries],
  );

  return <BlendCtx.Provider value={ctx}>{children}</BlendCtx.Provider>;
}

// ---------------------------------------------------------------------------
// Blend mesh entity — resolves BlendFile data, renders via MeshNode
// ---------------------------------------------------------------------------

function BlendMeshEntity({
  name,
  blendFileId,
}: {
  name: string;
  blendFileId?: string;
}) {
  const ctx = useContext(BlendCtx);
  const modifierOverrides = useStore((s) => s.modifierOverrides);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const sceneObjectsByName = useMemo(
    () => buildSceneObjectNameMap(sceneObjects),
    [sceneObjects],
  );
  const viewMode = useActiveRenderConfig((c) => c.viewMode);
  const isSculptTarget = useStore((s) => s.sculptMode?.objectId === name);
  const entryCache =
    blendFileId && ctx ? (ctx.entries.get(blendFileId) ?? null) : null;
  const blendFile = entryCache?.blendFile ?? null;
  const textureCache = entryCache?.textureCache ?? null;
  const morphKeysCache = entryCache?.morphKeysCache ?? null;
  const obj = entryCache?.objectsByName.get(name) ?? null;
  const mesh =
    blendFile && obj?.meshRef
      ? (blendFile.meshes.get(obj.meshRef) ?? null)
      : null;
  const morphKeys = obj?.shapeKeySetRef
    ? morphKeysCache?.get(obj.shapeKeySetRef)
    : undefined;
  const materials = useMemo(() => {
    if (!blendFile || !mesh) return null;
    const next = mesh.materialPtrs.map((ptr) =>
      ptr !== 0n ? (blendFile.materials.get(ptr) ?? null) : null,
    );
    if (next.length === 0) next.push(null);
    return next;
  }, [blendFile, mesh]);
  const mergedModifiers = useMemo(
    () =>
      obj
        ? mergeModifierStack(obj.modifiers, modifierOverrides.get(obj.name))
        : null,
    [obj, modifierOverrides],
  );
  const filteredModifiers = useMemo(
    () =>
      mergedModifiers?.filter(
        (modifier) => !(isSculptTarget && modifier.kind === "subsurf"),
      ) ?? null,
    [mergedModifiers, isSculptTarget],
  );
  if (
    !entryCache ||
    !blendFile ||
    !textureCache ||
    !obj ||
    obj.type !== ObjectType.Mesh ||
    !obj.meshRef ||
    !mesh ||
    !materials ||
    !filteredModifiers
  ) {
    return null;
  }

  return (
    <MeshNode
      name={obj.name}
      positions={mesh.positions}
      normals={mesh.loopNormals}
      indices={mesh.indices}
      uvs={mesh.uvs}
      topology={mesh.topology}
      localPosition={obj.localPosition}
      localRotation={obj.localRotation}
      localScale={obj.localScale}
      morphKeys={morphKeys}
      shapeKeySetRef={obj.shapeKeySetRef ?? undefined}
      materials={materials}
      materialGroups={mesh.materialGroups}
      textureCache={textureCache}
      viewMode={viewMode}
      modifiers={filteredModifiers}
      modifierSceneObjectsByName={sceneObjectsByName}
      modifierOverrides={modifierOverrides}
      blendEntryId={blendFileId}
    />
  );
}

// ---------------------------------------------------------------------------
// Scene mesh entity — delegates to PrimitiveNode
// ---------------------------------------------------------------------------

function SceneMeshEntity({ id }: { id: string }) {
  const obj = useStore((s) => s.sceneObjects.get(id));
  if (!obj) return null;
  return <PrimitiveNode obj={obj} />;
}

// ---------------------------------------------------------------------------
// Light entity — delegates to LightNode
// ---------------------------------------------------------------------------

function LightEntity({
  id,
  shadowMapSize,
}: {
  id: string;
  shadowMapSize: number;
}) {
  const obj = useStore((s) => s.sceneObjects.get(id));
  if (!obj) return null;
  return <LightNode obj={obj} shadowMapSize={shadowMapSize} />;
}

// ---------------------------------------------------------------------------
// Empty entity — delegates to EmptyNode
// ---------------------------------------------------------------------------

function EmptyEntity({ id }: { id: string }) {
  const obj = useStore((s) => s.sceneObjects.get(id));
  const overrides = useStore((s) => s.modifierOverrides.get(id));
  if (!obj) return null;
  const modifiers = mergeModifierStack([], overrides);
  if (modifierStackCanGenerateGeometryWithoutBase(modifiers)) {
    return <PrimitiveNode obj={obj} />;
  }
  return <EmptyNode obj={obj} />;
}

// ---------------------------------------------------------------------------
// Mannequin entity — delegates to MannequinNode (inside Suspense for GLB)
// ---------------------------------------------------------------------------

function MannequinEntity({ id }: { id: string }) {
  const obj = useStore((s) => s.sceneObjects.get(id));
  if (!obj) return null;
  return (
    <Suspense fallback={null}>
      <MannequinNode obj={obj} />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Image plane entity — delegates to ImagePlaneNode
// ---------------------------------------------------------------------------

function ImagePlaneEntity({ id }: { id: string }) {
  const obj = useStore((s) => s.sceneObjects.get(id));
  if (!obj) return null;
  return <ImagePlaneNode obj={obj} />;
}

// ---------------------------------------------------------------------------
// Per-entity dispatch
// ---------------------------------------------------------------------------

function EntityNode({
  entity,
  shadowMapSize,
}: {
  entity: Entity;
  shadowMapSize: number;
}) {
  // Visibility is handled by each node component internally — they subscribe
  // to the store's visibility map and control their own rendering/raycasting.
  // Pre-filtering here would change the scene graph structure and break
  // raycasting indices.

  switch (entity.kind) {
    case "mesh":
      return entity.origin === "blend" ? (
        <BlendMeshEntity name={entity.name} blendFileId={entity.blendFileId} />
      ) : (
        <SceneMeshEntity id={entityIdToStoreKey(entity.id)} />
      );

    case "light":
      return (
        <LightEntity
          id={entityIdToStoreKey(entity.id)}
          shadowMapSize={shadowMapSize}
        />
      );

    case "empty":
      return <EmptyEntity id={entityIdToStoreKey(entity.id)} />;

    case "mannequin":
      return <MannequinEntity id={entityIdToStoreKey(entity.id)} />;

    case "curve":
      return <SceneMeshEntity id={entityIdToStoreKey(entity.id)} />;

    case "image_plane":
      return <ImagePlaneEntity id={entityIdToStoreKey(entity.id)} />;

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Blend entry group — wraps all meshes from one blend file entry in a
// <group> that carries the entry's group transform (position, quaternion, scale).
// ---------------------------------------------------------------------------

function BlendEntryGroupNode({
  entryId,
  entities,
}: {
  entryId: string;
  entities: Entity[];
}) {
  const gt = useStore((s) => s.blendFiles.get(entryId)?.groupTransform);
  const groupRef = useRef<THREE.Group>(null);

  // Use the rotation array reference as the dep rather than individual float elements.
  // The store uses immutable updates, so gt.rotation is a new reference only when the
  // rotation actually changes — no spurious memo re-runs from floating-point noise.
  const quat = useMemo(
    () =>
      gt
        ? new THREE.Quaternion(
            gt.rotation[0],
            gt.rotation[1],
            gt.rotation[2],
            gt.rotation[3],
          )
        : new THREE.Quaternion(),
    [gt?.rotation, gt],
  );

  // Per-frame transform sync — reads the latest group transform directly from
  // the store, bypassing React's render batching.  This ensures blend-object
  // meshes are never a frame behind the data layer during live gizmo drags / snapping.
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    const latestGt = useStore
      .getState()
      .blendFiles.get(entryId)?.groupTransform;
    if (!latestGt) return;
    const [px, py, pz] = latestGt.position;
    g.position.set(px, py, pz);
    g.quaternion.set(
      latestGt.rotation[0],
      latestGt.rotation[1],
      latestGt.rotation[2],
      latestGt.rotation[3],
    );
    const [sx, sy, sz] = latestGt.scale;
    g.scale.set(sx, sy, sz);
  });

  if (!gt) return null;
  return (
    <group
      ref={groupRef}
      position={gt.position}
      quaternion={quat}
      scale={gt.scale}
    >
      {entities.map((entity) => (
        <BlendMeshEntity
          key={entity.id as string}
          name={entity.name}
          blendFileId={entryId}
        />
      ))}
    </group>
  );
}

// ---------------------------------------------------------------------------
// EntityRenderer — unified entry point
// ---------------------------------------------------------------------------

export function EntityRenderer({ shadowMapSize }: { shadowMapSize: number }) {
  const registry = useEntityRegistry();
  const { scene, gl } = useThree();

  initMaxAnisotropy(gl as unknown as THREE.WebGPURenderer);

  // Expose selection center computation for focusSelection.
  useEffect(() => {
    window.__poseref_computeSelectionCenter = (
      names: Set<string>,
    ): [number, number, number] | null => {
      let x = 0,
        y = 0,
        z = 0;
      let count = 0;
      const tmpBox = new THREE.Box3();
      const tmpCenter = new THREE.Vector3();
      for (const name of names) {
        const obj = scene.getObjectByName(name) as THREE.Mesh | null;
        if (!obj) continue;
        if (!obj.visible) continue;
        if (obj.geometry) {
          obj.geometry.computeBoundingBox();
          tmpBox.copy(obj.geometry.boundingBox!).applyMatrix4(obj.matrixWorld);
          tmpBox.getCenter(tmpCenter);
        } else {
          obj.getWorldPosition(tmpCenter);
        }
        x += tmpCenter.x;
        y += tmpCenter.y;
        z += tmpCenter.z;
        count++;
      }
      if (count === 0) return null;
      return [x / count, y / count, z / count];
    };
    return () => {
      delete window.__poseref_computeSelectionCenter;
    };
  }, [scene]);

  // Partition entities: blend mesh entities grouped by entry, others rendered individually
  const { blendGroups, otherEntities } = useMemo(() => {
    const blendGroups = new Map<string, Entity[]>();
    const otherEntities: Entity[] = [];
    for (const entity of registry.values()) {
      if (
        entity.kind === "mesh" &&
        entity.origin === "blend" &&
        entity.blendFileId
      ) {
        let group = blendGroups.get(entity.blendFileId);
        if (!group) {
          group = [];
          blendGroups.set(entity.blendFileId, group);
        }
        group.push(entity);
      } else {
        otherEntities.push(entity);
      }
    }
    return { blendGroups, otherEntities };
  }, [registry]);

  return (
    <BlendContextProvider>
      <group>
        {Array.from(blendGroups.entries()).map(([entryId, entities]) => (
          <BlendEntryGroupNode
            key={entryId}
            entryId={entryId}
            entities={entities}
          />
        ))}
        {otherEntities.map((entity) => (
          <EntityNode
            key={entity.id as string}
            entity={entity}
            shadowMapSize={shadowMapSize}
          />
        ))}
      </group>
    </BlendContextProvider>
  );
}
